import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, AnimatePresence } from "framer-motion";
import { Palmtree, ArrowUpRight, Mail, Phone, Globe } from "lucide-react";
import axios from "axios";
import { API } from "@/constants";

const VIEW_W = 1000;
const VIEW_H = 560;

// Europe viewport projection (lon -12..34, lat 60..32)
const euX = (lon) => 250 + ((lon + 12) / 46) * 460;
const euY = (lat) => 130 + ((60 - lat) / 28) * 300;
// Origin (Kerala) viewport projection (lon 60..95, lat 0..28)
const orgX = (lon) => 790 + ((lon - 60) / 35) * 140;
const orgY = (lat) => 520 - (lat / 28) * 110;

const FALLBACK_SETTINGS = {
  origin_name: "Kerala",
  origin_country: "India",
  origin_label: "God's Own Country",
  origin_latitude: 9.93,
  origin_longitude: 76.27,
  headline: "From Kerala to Europe",
  supporting_text: "Carrying the flavours of God's Own Country to tables across Europe.",
  route_animation: true,
  show_origin: true,
  show_connections: true,
};

const FALLBACK_HUBS = [
  { id: "ie", country_name: "Ireland", city: "Dublin", hub_title: "The Atlantic Gateway", short_description: "Where KERA's European journey began.", latitude: 53.35, longitude: -6.26, journey_order: 1 },
  { id: "gb", country_name: "United Kingdom", city: "London", hub_title: "The Capital Table", short_description: "Kerala's spice heritage meets London's table.", latitude: 51.5, longitude: -0.12, journey_order: 2 },
  { id: "de", country_name: "Germany", city: "Frankfurt", hub_title: "The Central Depot", short_description: "A central European home for God's Own flavours.", latitude: 50.11, longitude: 8.68, journey_order: 3 },
  { id: "mt", country_name: "Malta", city: "Valletta", hub_title: "The Mediterranean Flagship", short_description: "Two island food cultures, one shared table.", latitude: 35.9, longitude: 14.51, journey_order: 4 },
];

// Stylised warm-neutral European line work (artistic, not cartographic)
const MAINLAND =
  "M 270 355 L 285 302 L 318 288 L 330 252 L 372 240 L 398 224 L 430 210 L 448 162 L 486 168 L 520 150 L 560 132 L 620 126 L 672 140 L 676 186 L 620 218 L 640 260 L 662 300 L 640 330 L 600 370 L 585 352 L 555 358 L 530 378 L 512 352 L 500 328 L 478 300 L 442 306 L 410 340 L 340 380 L 312 388 L 280 372 Z";
const BRITAIN =
  "M 380 222 L 386 208 L 370 196 L 356 182 L 348 160 L 326 146 L 316 160 L 330 176 L 322 196 L 326 218 L 318 236 L 336 246 L 362 240 Z";
const IRELAND_ISLE = "M 300 216 L 318 204 L 314 188 L 296 184 L 282 196 L 284 212 Z";
const SICILY = "M 508 372 L 530 368 L 522 380 Z";

function buildRoute(origin, points, showOrigin, showConnections) {
  if (!points.length) return "";
  if (!showOrigin && !showConnections) return "";
  const pts = showOrigin ? [origin, ...points] : points;
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const p = pts[i];
    const cx = (prev.x + p.x) / 2;
    const cy = Math.min(prev.y, p.y) - Math.max(24, Math.abs(prev.x - p.x) * 0.18);
    d += ` Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
  }
  return d;
}

export default function JourneyMap() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.3 });
  const reducedMotion = useReducedMotion();

  const [hubs, setHubs] = useState(FALLBACK_HUBS);
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hubs`).then((r) => {
      if (r.data?.hubs?.length) setHubs(r.data.hubs);
    }).catch(() => {});
    axios.get(`${API}/journey/settings`).then((r) => {
      if (r.data?.headline) setSettings((s) => ({ ...s, ...r.data }));
    }).catch(() => {});
  }, []);

  const points = useMemo(
    () =>
      [...hubs]
        .sort((a, b) => (a.journey_order ?? 0) - (b.journey_order ?? 0))
        .map((h) => ({ ...h, x: euX(h.longitude), y: euY(h.latitude) })),
    [hubs]
  );

  const origin = useMemo(
    () => ({ x: orgX(settings.origin_longitude), y: orgY(settings.origin_latitude) }),
    [settings.origin_longitude, settings.origin_latitude]
  );

  const routeD = useMemo(
    () => buildRoute(origin, points, settings.show_origin, settings.show_connections),
    [origin, points, settings.show_origin, settings.show_connections]
  );

  const animate = inView && settings.route_animation && !reducedMotion;
  const activeId = hoveredId || selectedId;
  const activeHub = points.find((p) => p.id === activeId);

  return (
    <section id="journey" ref={sectionRef} className="py-20 lg:py-32 relative overflow-hidden" data-testid="journey-section">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-kera-copper mb-6"
        >
          The Brand Journey
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display font-semibold tracking-tight text-kera-ink text-2xl sm:text-3xl lg:text-4xl"
          data-testid="journey-headline"
        >
          {settings.headline}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-4 max-w-2xl text-base sm:text-lg text-kera-muted leading-relaxed"
          data-testid="journey-subtext"
        >
          {settings.supporting_text}
        </motion.p>

        {/* Mobile country selector */}
        <div className="md:hidden mt-8 -mx-4 px-4 overflow-x-auto" data-testid="hub-country-selector">
          <div className="flex gap-2 w-max pb-2">
            {points.map((h) => (
              <button
                key={h.id}
                data-testid={`hub-pill-${h.id}`}
                onClick={() => setSelectedId(h.id)}
                className={`whitespace-nowrap rounded-full px-5 py-2.5 text-xs font-mono uppercase tracking-[0.15em] border transition-colors duration-300 ${
                  (selectedId || points[0]?.id) === h.id
                    ? "bg-kera-gold text-kera-ink border-kera-gold"
                    : "border-kera-line text-kera-muted bg-kera-card"
                }`}
              >
                {h.country_name}
              </button>
            ))}
          </div>
        </div>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.2 }}
          className="relative mt-6 md:mt-12 w-full"
          style={{ aspectRatio: `${VIEW_W}/${VIEW_H}` }}
          data-testid="journey-map-container"
        >
          <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="absolute inset-0 w-full h-full" role="img" aria-label="KERA journey map from Kerala to Europe">
            {/* Europe line work */}
            <g stroke="#DED3C1" strokeWidth="1.4" fill="#F2EBE0" fillOpacity="0.85">
              <path d={MAINLAND} strokeLinejoin="round" />
              <path d={BRITAIN} strokeLinejoin="round" />
              <path d={IRELAND_ISLE} strokeLinejoin="round" />
              <path d={SICILY} strokeLinejoin="round" />
              <ellipse cx="462" cy="336" rx="8" ry="14" />
              <ellipse cx="472" cy="314" rx="5" ry="9" />
            </g>
            <text x="470" y="310" textAnchor="middle" fontFamily="'Playfair Display', serif" fontSize="42" fill="#E6DEC3" letterSpacing="14" style={{ pointerEvents: "none" }}>
              EUROPE
            </text>

            {/* Route */}
            {routeD && (
              <>
                <path d={routeD} fill="none" stroke="#C5A059" strokeOpacity="0.22" strokeWidth="1.5" strokeDasharray="3 6" />
                <motion.path
                  d={routeD}
                  fill="none"
                  stroke="#C5A059"
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="kera-route-glow"
                  data-testid="journey-route-path"
                  initial={false}
                  animate={{ pathLength: animate ? 1 : inView || reducedMotion ? 1 : 0 }}
                  transition={animate ? { duration: 4.2, delay: 0.8, ease: "easeInOut" } : { duration: 0.6 }}
                  style={{ pathLength: animate ? undefined : 1 }}
                  {...(animate ? { initial: { pathLength: 0 } } : {})}
                />
              </>
            )}

            {/* Kerala origin */}
            {settings.show_origin && (
              <motion.g
                data-testid="map-marker-kerala"
                initial={animate ? { opacity: 0, scale: 0 } : false}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: animate ? 0.2 : 0, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformOrigin: `${origin.x}px ${origin.y}px` }}
              >
                <circle cx={origin.x} cy={origin.y} r="14" fill="none" stroke="#C5A059" strokeWidth="1.2" className="kera-marker-pulse" />
                <circle cx={origin.x} cy={origin.y} r="6" fill="#C5A059" />
                <circle cx={origin.x} cy={origin.y} r="2.5" fill="#1A1916" />
                <text x={origin.x} y={origin.y + 30} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="11" letterSpacing="3" fill="#8C6D1F" style={{ pointerEvents: "none" }}>
                  {settings.origin_name.toUpperCase()}, {settings.origin_country.toUpperCase()}
                </text>
                <text x={origin.x} y={origin.y + 46} textAnchor="middle" fontFamily="'Playfair Display', serif" fontSize="13" fill="#5E5A52" style={{ pointerEvents: "none" }}>
                  {settings.origin_label}
                </text>
              </motion.g>
            )}

            {/* Hub markers */}
            {points.map((h, i) => {
              const delay = animate ? 1.2 + ((i + 1) / (points.length + 1)) * 4.2 : 0;
              const isActive = activeId === h.id;
              return (
                <motion.g
                  key={h.id}
                  data-testid={`map-marker-${h.id}`}
                  initial={animate ? { opacity: 0, scale: 0 } : false}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  style={{ transformOrigin: `${h.x}px ${h.y}px`, cursor: "pointer" }}
                  onMouseEnter={() => setHoveredId(h.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => setSelectedId(h.id)}
                >
                  <circle cx={h.x} cy={h.y} r="22" fill="transparent" />
                  <circle cx={h.x} cy={h.y} r="11" fill="none" stroke={isActive ? "#8C6D1F" : "#C5A059"} strokeWidth="1.2" className="kera-marker-pulse" />
                  <circle cx={h.x} cy={h.y} r={isActive ? 6.5 : 5} fill={isActive ? "#8C6D1F" : "#C5A059"} style={{ transition: "r 0.3s, fill 0.3s" }} />
                  <circle cx={h.x} cy={h.y} r="2" fill="#FFFDF9" />
                  <text
                    x={h.x}
                    y={i % 2 === 0 ? h.y - 16 : h.y + 26}
                    textAnchor="middle"
                    fontFamily="'JetBrains Mono', monospace"
                    fontSize="9"
                    letterSpacing="1.5"
                    fill={isActive ? "#8C6D1F" : "#5E5A52"}
                    style={{ pointerEvents: "none" }}
                  >
                    {h.country_name.toUpperCase()}
                  </text>
                </motion.g>
              );
            })}
          </svg>

          {/* Palm cue near origin */}
          {settings.show_origin && (
            <div
              className="absolute text-kera-gold pointer-events-none"
              style={{ left: `${(origin.x / VIEW_W) * 100}%`, top: `${((origin.y - 44) / VIEW_H) * 100}%`, transform: "translateX(-50%)" }}
            >
              <Palmtree size={20} strokeWidth={1.5} />
            </div>
          )}

          {/* Desktop hover card */}
          <AnimatePresence>
            {activeHub && (
              <motion.div
                key={activeHub.id}
                initial={{ opacity: 0, y: 12, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.98 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="hidden md:block absolute z-20 w-72"
                style={{
                  left: `clamp(150px, ${(activeHub.x / VIEW_W) * 100}%, calc(100% - 150px))`,
                  top: `${(activeHub.y / VIEW_H) * 100}%`,
                  transform: "translate(-50%, calc(-100% - 22px))",
                }}
                data-testid={`hub-info-card-${activeHub.id}`}
              >
                <HubCard hub={activeHub} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Mobile card */}
        <div className="md:hidden mt-6" data-testid="hub-mobile-card">
          <AnimatePresence mode="wait">
            {(activeHub || points[0]) && (
              <motion.div
                key={(activeHub || points[0]).id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
              >
                <HubCard hub={activeHub || points[0]} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function HubCard({ hub }) {
  return (
    <div className="bg-kera-card/95 backdrop-blur-md border border-kera-line rounded-2xl shadow-xl overflow-hidden">
      {hub.hub_image && (
        <img src={hub.hub_image} alt={hub.country_name} className="w-full h-28 object-cover" />
      )}
      <div className="p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-kera-copper">
          {hub.country_name}
          {hub.city ? ` — ${hub.city}` : ""}
          {hub.region ? `, ${hub.region}` : ""}
        </p>
        <h3 className="font-display text-xl text-kera-ink mt-1.5">{hub.hub_title || hub.country_name}</h3>
        {hub.short_description && (
          <p className="text-sm text-kera-muted mt-2 leading-relaxed">{hub.short_description}</p>
        )}
        {hub.partner_name && (
          <p className="mt-3 text-xs text-kera-muted">
            <span className="font-mono uppercase tracking-[0.15em] text-kera-copper">Partner</span> · {hub.partner_name}
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-kera-muted">
          {hub.contact_email && (
            <span className="inline-flex items-center gap-1"><Mail size={12} /> {hub.contact_email}</span>
          )}
          {hub.contact_phone && (
            <span className="inline-flex items-center gap-1"><Phone size={12} /> {hub.contact_phone}</span>
          )}
          {hub.website_url && (
            <span className="inline-flex items-center gap-1"><Globe size={12} /> {hub.website_url.replace(/^https?:\/\//, "")}</span>
          )}
        </div>
        <a
          href={hub.website_url || "#contact"}
          data-testid={`hub-cta-${hub.id}`}
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-kera-ink border-b border-kera-gold pb-0.5 hover:text-kera-copper hover:border-kera-copper transition-colors duration-300"
        >
          Discover KERA <ArrowUpRight size={13} />
        </a>
      </div>
    </div>
  );
}
